/**
 * ENG-9708 — a managed MCP server is pruned only after it has been absent for N
 * CONSECUTIVE poll cycles, not on the first one.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT HAPPENED (2026-08-31, prod)
 * ─────────────────────────────────────────────────────────────────────────
 * A Composio per-key IP allowlist was applied to the Integrity Labs org key as a
 * controlled test. The API Lambda has no allowlistable egress, so its Composio
 * calls 401'd and `/host/managed-toolkits` returned WELL-FORMED responses that
 * simply contained no Composio toolkits.
 *
 * Between 10:37:37Z and 10:39:33Z the manager pruned 24 `composio_*` MCP servers
 * across 6+ agents. The allowlist came off ~20 minutes later and THE PRUNE DID
 * NOT REVERSE: agents restarted, `.mcp.json` was rewritten at 11:09 still with
 * zero `composio_*` keys, and recovery took a manual bulk-reconnect.
 *
 * The asymmetry is the whole complaint. The trigger applied in ~60s and reverted
 * in ~60s; undoing its effect took a manual reconnect across 6+ agents and ~24
 * integrations.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THE EXISTING GUARDS COULD NOT STOP IT
 * ─────────────────────────────────────────────────────────────────────────
 * ENG-9140's cross-agent ledger fired twice and named the cause exactly — "this
 * may be an untrustworthy toolkit response rather than a real one". It is
 * deliberately a REPORT, not a veto, so that a genuine mass revocation is not
 * stranded.
 *
 * The shape check in `fetchManagedToolkits` is where untrustworthy answers are
 * meant to be stopped, and it is not at fault: the response was not malformed,
 * it was well-formed and SEMANTICALLY WRONG. The API correctly reported "these
 * agents are covered and they have no Composio toolkits", because the API itself
 * could not reach Composio. No shape check can tell that from a real revocation.
 *
 * So the missing dimension is neither breadth (ENG-9140 has that) nor shape. It
 * is DURATION, and that is all this module adds.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE TRAP IN THE OBVIOUS IMPLEMENTATION — read before changing the call site
 * ─────────────────────────────────────────────────────────────────────────
 * The prune loop in `manager-worker.ts` lives INSIDE `if (mcpHash !== prevMcpHash)`,
 * and the new hash is stored at the end of that same block. So counting misses
 * at the prune site is INERT:
 *
 *   cycle 1 — toolkit missing, hash changed, defer (streak 1), new hash stored
 *   cycle 2 — still missing, hash now EQUALS the stored hash, block does not run
 *   ...     — streak never matures, and nothing is ever pruned, including
 *             genuine revocations
 *
 * The observation must therefore happen on EVERY cycle, outside that guard, and
 * so must the prune — a streak that matures while the hash is stable has no
 * other opportunity to fire. `observe()` is written to be called
 * unconditionally, and there is a test that fails if it is not.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * A DEGRADED ANSWER vs A FAILED-TO-RUN ONE — they ARE distinguishable here
 * ─────────────────────────────────────────────────────────────────────────
 * The fair objection to any consecutive-cycles guard: if the call site cannot
 * tell a real negative from an absent measurement, N cycles only makes it
 * confidently wrong more slowly.
 *
 * At THIS call site it can, and structurally rather than carefully. `observe()`
 * runs only after `managedToolkits.forAgent()` has RETURNED. Every
 * not-an-answer path throws before that point:
 *
 *   - `toolkits` missing or not an array — the `{}` an unparseable 200 produces
 *     — throws UntrustworthyToolkitResponseError (ENG-9140);
 *   - a failed batch falls back to a per-agent fetch, which throws the same way
 *     if it is also unanswerable;
 *   - a response that ASSERTS coverage and omits the single agent it was asked
 *     about is a contradiction and throws.
 *
 * A throw lands in the convergence block's catch, which logs and skips the agent
 * for the cycle: no write, no prune, no `observe()`. So an absent measurement is
 * never counted as a negative, and the residual this module exists for — a
 * WELL-FORMED response that is semantically wrong because the API itself could
 * not reach the provider — is exactly the case no shape check can catch and only
 * duration can.
 *
 * THE ASYMMETRY, deliberate: a skipped cycle does not advance the streak, and it
 * does not RESET it either. Resetting on no-information would let a permanently
 * flaky API veto every prune forever — the ENG-7994 failure in the other
 * direction, where nora's revoked composio_googledocs stayed in her config and
 * its dead remote MCP wedged her turns until it was removed by hand. The streak
 * counts N consecutive OBSERVED absences; silence is evidence for neither side.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * IN-MEMORY, DELIBERATELY
 * ─────────────────────────────────────────────────────────────────────────
 * A manager restart resets every streak. That is the conservative direction: a
 * restart delays a legitimate prune by a few minutes and can never cause a
 * spurious one. Persisting streaks would trade that safety for tidiness.
 */

/** Default confirmations required before a prune is allowed. */
export const DEFAULT_PRUNE_CONFIRM_CYCLES = 5;

/**
 * Read the threshold from the environment.
 *
 * A value below 1 is rejected rather than clamped-to-1 silently: `0` means
 * someone is trying to disable the guard, and the honest response is to keep the
 * default and let them notice, not to quietly grant a weaker version of what
 * they asked for. Anything unparseable falls back for the same reason.
 *
 * **The whole string must be an integer** — this used to be `Number.parseInt`,
 * which stops at the first non-digit and returns the PREFIX. So `1.5`, `1suffix`
 * and `1e3` all parsed to `1`, and a threshold of 1 is precisely the shipped bug
 * this module exists to fix: prune on a single absent observation. A fat-fingered
 * env value silently reverted the guard to the behaviour it replaced, with no
 * error and nothing in the log to say so.
 *
 * A digit string too large to be a safe integer also falls back. That is the one
 * case where falling back is arguably weaker than what was asked (someone writing
 * twenty digits means "never prune" and gets 5), but such a value is not a real
 * configuration, and the alternative — trusting a number that has already lost
 * precision — is worse. Stated here so the behaviour is chosen rather than
 * inherited from `Number`.
 */
export function pruneConfirmCycles(
  env: NodeJS.ProcessEnv = process.env,
): number {
  const raw = env['AUGMENTED_MANAGED_PRUNE_CONFIRM_CYCLES'];
  if (raw === undefined) return DEFAULT_PRUNE_CONFIRM_CYCLES;
  const trimmed = raw.trim();
  // Digits only, end to end. Rejects '1.5' / '1suffix' / '1e3' / '+1' / '-1',
  // each of which a prefix parser would have accepted as some smaller number.
  if (!/^\d+$/.test(trimmed)) return DEFAULT_PRUNE_CONFIRM_CYCLES;
  const n = Number(trimmed);
  if (!Number.isSafeInteger(n) || n < 1) return DEFAULT_PRUNE_CONFIRM_CYCLES;
  return n;
}

/** What `observe()` decided about one absent key. */
export interface PruneDecision {
  serverKey: string;
  /** Consecutive cycles this key has been absent, including this one. */
  streak: number;
  /** True only when the streak has reached the confirmation threshold. */
  mature: boolean;
}

/**
 * Per-agent consecutive-miss counters for managed MCP server keys.
 *
 * Keyed `agentId` -> `serverKey` -> streak. Nothing is stored for a key that is
 * present; presence deletes the entry outright, so a stable fleet costs nothing
 * and there is no drift between "streak 0" and "no streak".
 */
export class ManagedPruneStreaks {
  private readonly streaks = new Map<string, Map<string, number>>();

  constructor(private readonly threshold: number = DEFAULT_PRUNE_CONFIRM_CYCLES) {}

  /**
   * Record one poll cycle for one agent.
   *
   * MUST BE CALLED ON EVERY CYCLE, including cycles where nothing changed —
   * see the trap above. `declaredManagedKeys` is what the agent's config
   * currently declares; `expectedKeys` is what the API says it should have.
   *
   * A key present in `expectedKeys` has its streak cleared IMMEDIATELY, which is
   * what makes a toolkit's reappearance undo the wait rather than merely pause
   * it.
   *
   * Returns a decision per absent key, in the order given, so the caller can log
   * the deferrals as well as act on the matured ones.
   */
  observe(
    agentId: string,
    declaredManagedKeys: readonly string[],
    expectedKeys: ReadonlySet<string>,
  ): PruneDecision[] {
    let perAgent = this.streaks.get(agentId);

    // A key that came back resets to zero. Done for EVERY expected key rather
    // than only the declared ones, so a toolkit that reappears before its config
    // entry is rewritten still clears its streak.
    if (perAgent) {
      for (const key of expectedKeys) perAgent.delete(key);
    }

    const decisions: PruneDecision[] = [];
    for (const key of declaredManagedKeys) {
      if (expectedKeys.has(key)) continue;
      if (!perAgent) {
        perAgent = new Map<string, number>();
        this.streaks.set(agentId, perAgent);
      }
      const streak = (perAgent.get(key) ?? 0) + 1;
      perAgent.set(key, streak);
      decisions.push({ serverKey: key, streak, mature: streak >= this.threshold });
    }

    // A key we are no longer tracking (removed from the config by any route)
    // should not keep a stale counter around.
    if (perAgent) {
      const declared = new Set(declaredManagedKeys);
      for (const key of [...perAgent.keys()]) {
        if (!declared.has(key)) perAgent.delete(key);
      }
      if (perAgent.size === 0) this.streaks.delete(agentId);
    }

    return decisions;
  }

  /** Current streak for one key. Zero when untracked. */
  streakFor(agentId: string, serverKey: string): number {
    return this.streaks.get(agentId)?.get(serverKey) ?? 0;
  }

  /** Drop everything for an agent — used when an agent leaves the host. */
  forget(agentId: string): void {
    this.streaks.delete(agentId);
  }

  /** Number of agents currently carrying at least one streak (tests/diagnostics). */
  get trackedAgents(): number {
    return this.streaks.size;
  }
}
